

import { FC, HTMLInputTypeAttribute, useCallback, useEffect, useMemo, useState } from 'react';

import { TextField } from '@mui/material';
import { InputTextFieldProps } from './types/FieldTypes';

export interface ITextFieldProps extends InputTextFieldProps {
    type?: HTMLInputTypeAttribute
}

const InputTextField: FC<ITextFieldProps> = ({
    FieldInputProps: { state: { value }, actions: {  update, blur, focus, touch } },
    fullWidth,
    onChange,
    sx,
    inputProps: { onFocus: inputPropsFocus, onBlur: inputPropsBlur, ...inputProps } = {},
    error,
    ...rest
}) => {
    // This solves a weird bug in react where the location of the cursor in a text field would change
    // due to the response of the state update versus the speed a person types. 
    const [localValue, setLocalValue] = useState(() => value ?? '');
    useEffect(() => {
        setLocalValue(value ?? '');
    }, [value, setLocalValue]);
    
    // Handles the update of the value, updating local and global form properties
    const onChangeCombined = useCallback((event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setLocalValue(event.target.value); 

        if (onChange) {
            onChange(event);
        }

        update(event.target.value && event.target.value !== '' ? event.target.value : undefined as any, true, true, true);
    }, [onChange, update, setLocalValue]);

    // Handles the focus features of the form field
    const onFocusCombined = useCallback((event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (inputPropsFocus) {
            inputPropsFocus(event);
        }

        focus();
    }, [inputPropsFocus, focus]);

    // Handles the on blur features of the form field
    const onBlurCombined = useCallback((event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (inputPropsBlur) {
            inputPropsBlur(event);
        }

        blur();
        touch();
    }, [inputPropsBlur, blur, touch]);

    // Prevent mouse wheel from changing number input
    const handleWheel = useCallback((event: React.WheelEvent<HTMLInputElement>) => {
        if (rest.type === "number") {
            event.currentTarget.blur();
            event.preventDefault();
        }
    }, [rest.type]);

    // Prevent arrow keys from changing number input
    const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (rest.type === "number" && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
            event.preventDefault();
        }
    }, [rest.type]);

    const customSx = useMemo(() => {
        return {
            ...sx,
            'input[type=number]::-webkit-inner-spin-button': {
                '-webkit-appearance': 'none'
            },
            'input[type=number]::-webkit-outer-spin-button': {
                '-webkit-appearance': 'none'
            },
            
            margin: '0',
            'input[type=number]': {
                '-moz-appearance': 'textfield'
            }
        };
    }, [sx]);

    return ( 
        <TextField
            type={rest.type ?? "text"}
            {...rest as any}
            fullWidth={fullWidth === undefined ? true : fullWidth}
            size='small'
            inputProps={{
                ...inputProps,
                onBlur: onBlurCombined,
                onFocus: onFocusCombined,
                onWheel: handleWheel,
                onKeyDown: handleKeyDown
            }}
            value={localValue}
            onChange={onChangeCombined}
            sx={{...customSx}}
        />
    );
}

export default InputTextField